import {
  Autocomplete,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { listCategories, listUnlistedProducts } from '../../api/business/endpoints';
import type { IntegrationLink, ListingSelection, UnlistedProduct } from '../../api/business/types';
import { flattenCategories } from '../../components/categoryTree';
import { FormDialog } from '../../components/FormDialog';
import { useLanguage } from '../../i18n/LanguageContext';

type PublishBy = 'products' | 'category' | 'all';

/** What to publish to a shop (R0030 Q-08): products it does not sell yet, a category's, or all. */
export const PublishListingsDialog = ({
  link,
  listed,
  onClose,
  onSubmit,
  isBusy,
  error,
}: {
  link: IntegrationLink;
  listed: Set<string>;
  onClose: () => void;
  onSubmit: (selection: ListingSelection) => void;
  isBusy: boolean;
  error: unknown;
}) => {
  const { t } = useLanguage();
  const [by, setBy] = useState<PublishBy>('products');
  const [products, setProducts] = useState<Array<UnlistedProduct>>([]);
  const [categoryId, setCategoryId] = useState('');
  const unlisted = useQuery({
    queryKey: ['integration-unlisted', link.id],
    queryFn: () => listUnlistedProducts(link.companyId, link.id),
  });
  const categories = useQuery({
    queryKey: ['categories', link.companyId],
    queryFn: () => listCategories(link.companyId),
  });
  const choices = (unlisted.data ?? []).filter((product) => !listed.has(product.id));
  const ready = by === 'all' || (by === 'products' ? products.length > 0 : categoryId !== '');

  return (
    <FormDialog
      title={t('listings.publishTitle', { name: link.name })}
      onClose={onClose}
      onSubmit={() =>
        ready &&
        onSubmit(
          by === 'all'
            ? { all: true }
            : by === 'category'
              ? { categoryId }
              : { productIds: products.map((product) => product.id) },
        )
      }
      submitLabel={t('listings.publish')}
      isBusy={isBusy || !ready}
      error={error ?? unlisted.error ?? categories.error}
    >
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          {t('listings.publishHint')}
        </Typography>
        <RadioGroup value={by} onChange={(event) => setBy(event.target.value as PublishBy)}>
          <FormControlLabel value="products" control={<Radio />} label={t('listings.byProducts')} />
          <FormControlLabel value="category" control={<Radio />} label={t('listings.byCategory')} />
          <FormControlLabel value="all" control={<Radio />} label={t('listings.byAll')} />
        </RadioGroup>
        {by === 'products' ? (
          <Autocomplete
            multiple
            options={choices}
            value={products}
            loading={unlisted.isLoading}
            onChange={(_event, value) => setProducts(value)}
            getOptionLabel={(product) => [product.name, product.sku].filter(Boolean).join(' · ')}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} label={t('listings.products')} />}
          />
        ) : null}
        {by === 'category' ? (
          <TextField
            select
            label={t('listings.category')}
            value={categoryId}
            onChange={(event) => setCategoryId(event.target.value)}
          >
            {flattenCategories(categories.data ?? []).map(({ category, path }) => (
              <MenuItem key={category.id} value={category.id}>
                {path.join(' › ')}
              </MenuItem>
            ))}
          </TextField>
        ) : null}
      </Stack>
    </FormDialog>
  );
};
